/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AdaptiveFormShell, DraftRecoveryNotice, formatDraftTime } from '@/shared/ui/components/form';
import { cn } from '@/shared/ui/utils/tailwind';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { usePropertyDrawerForm } from './usePropertyDrawerForm';
import { DadosTab } from './DadosTab';
import { DescricaoTab } from './DescricaoTab';
import { PropertyPreviewBar } from './PropertyPreviewBar';
import { TabNavigation, type TabId } from './TabNavigation';
import { AgencyRequiredFieldsContext } from './agency-required-context';
import { SubmitButton } from './SubmitButton';
import { PublishDialog } from '../PublishDialog';
import { syncPortalPublications } from '../PropertyFormDualPane/use-property-form-submit';
import { getValidAuthToken } from '@services/auth/auth';
import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { FF_APP_FACEBOOK, FF_APP_PORTALS_INTEGRATION } from '@/shared/utils/feature-flag';
import { collectImageRefs } from '@/shared/utils/portal-publish';
import { isDevFormBridgeEnabled } from '@/shared/utils/dev-form-bridge';
import { SECTION_TAB, scrollToSection, type PropertyDrawerSection } from './drawerSections';

const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

export interface PropertyDrawerProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
  editingProperty?: Record<string, any> | null;
  onSuccess?: () => void;
  /**
   * Opened to change a publication (OLX "Editar"): saving must update the
   * existing publications, so "Sincronizar posts ao salvar" starts checked
   * even if the publish-status read fails.
   */
  syncPublications?: boolean;
  /** Section to open the editor onto, from a detail-page card's pencil. */
  focusSection?: PropertyDrawerSection;
}

const validationSummaryStyle: CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  background: 'color-mix(in srgb, var(--status-error) 8%, transparent)',
  border: '1px solid color-mix(in srgb, var(--status-error) 20%, transparent)',
  fontSize: 12,
  fontFamily: 'var(--font-family)',
  color: 'var(--status-error)',
};

/**
 * Renders the property create/edit drawer with tabbed form sections.
 * @returns The property drawer element.
 */
export function PropertyDrawer({ open, onClose, agencyId, editingProperty, onSuccess, syncPublications = false, focusSection }: PropertyDrawerProps) {
  const { t, i18n } = useTranslation('features/properties');
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<TabId>('dados');
  const [syncOrPublish, setSyncOrPublish] = useState(true);
  const [hasActivePublications, setHasActivePublications] = useState(false);
  const [publishDialogPropertyId, setPublishDialogPropertyId] = useState<string | null>(null);

  const showPortalActions = FF_APP_FACEBOOK || FF_APP_PORTALS_INTEGRATION;

  const loadPublicationStatus = useCallback(async (propId: string) => {
    try {
      const token = await getValidAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.get(
        `${API_BASE}/portal-integration/agencies/${agencyId}/properties/${propId}/publish-status`,
        { headers },
      ).catch(() => null);
      const portals = res?.data?.portals || {};
      const active = Object.keys(portals).length > 0 || syncPublications;
      setHasActivePublications(active);
      setSyncOrPublish(active);
    } catch {
      // keep defaults
    }
  }, [agencyId, syncPublications]);

  useEffect(() => {
    if (!open || !showPortalActions) return;
    if (editingProperty?.id) {
      if (syncPublications) {
        setHasActivePublications(true);
        setSyncOrPublish(true);
      }
      loadPublicationStatus(editingProperty.id);
    } else {
      setHasActivePublications(false);
      setSyncOrPublish(false);
    }
  }, [open, editingProperty?.id, loadPublicationStatus, showPortalActions, syncPublications]);

  useEffect(() => {
    if (!open || !focusSection) return undefined;
    setActiveTab(SECTION_TAB[focusSection]);
    return scrollToSection(focusSection);
  }, [open, focusSection]);

  const handleAfterSave = useCallback(async (propertyId: string) => {
    if (!syncOrPublish || !showPortalActions) return;
    if (hasActivePublications) {
      await syncPortalPublications(agencyId, propertyId, t);
    } else {
      setPublishDialogPropertyId(propertyId);
    }
  }, [syncOrPublish, hasActivePublications, agencyId, t, showPortalActions]);

  const {
    form, isSubmitting, erroredTabs, validationMessage,
    handleSubmit, lookupCep, pendingImagesRef, reorderedImagesRef, isEditMode, requiredFields,
    draftOffer, restoreDraft, discardDraft, mediaKey,
  } = usePropertyDrawerForm({
    agencyId,
    open,
    editingProperty,
    afterSave: handleAfterSave,
    onSuccess: () => { onClose(); onSuccess?.(); },
  });

  useEffect(() => {
    if (isDevFormBridgeEnabled() && open) {
      (window as any).__DEV_PROPERTY_FORM__ = {
        setValue: form.setValue, getValues: form.getValues,
        trigger: form.trigger, submit: handleSubmit,
      };
    }
    return () => { (window as any).__DEV_PROPERTY_FORM__ = undefined; };
  }, [open, form, handleSubmit]);

  const title = t(isEditMode ? 'form.drawer.editProperty' : 'form.drawer.newProperty');
  const buttonLabel = t(isEditMode ? 'form.drawer.updateButton' : 'form.drawer.createButton');
  const watchedValues = form.watch();

  const descricaoProps = {
    form, pendingImagesRef, agencyId,
    propertyId: editingProperty?.id,
    existingImageKeys: editingProperty?.images,
    onImageOrderChange: (urls: string[]) => { reorderedImagesRef.current = urls; },
    syncOrPublish, onSyncOrPublishChange: setSyncOrPublish,
    hasActivePublications, showPortalActions,
    focusSection: open ? focusSection : undefined,
  };

  return (
    <>
      <AdaptiveFormShell
        open={open}
        onClose={onClose}
        title={title}
        footer={
          <SubmitButton onClick={handleSubmit} disabled={isSubmitting}
            label={buttonLabel} savingLabel={t('form.drawer.saving')} />
        }
      >
        <AgencyRequiredFieldsContext.Provider value={requiredFields}>
          <div className="flex flex-col gap-0">
            {open && draftOffer && (
              <DraftRecoveryNotice
                message={t('form.drawer.draftRecovered', { when: formatDraftTime(draftOffer.savedAt, i18n.language) })}
                restoreLabel={t('form.drawer.draftRestore')}
                discardLabel={t('form.drawer.draftDiscard')}
                onRestore={restoreDraft}
                onDiscard={discardDraft}
              />
            )}
            {isMobile ? (
              <>
                <TabNavigation activeTab={activeTab} onChange={setActiveTab} erroredTabs={erroredTabs} />
                {activeTab === 'dados' && <DadosTab form={form} onCepBlur={lookupCep} agencyId={agencyId} propertyId={editingProperty?.id} />}
                {activeTab === 'descricao' && <DescricaoTab key={mediaKey} {...descricaoProps} showMobileHeading={true} />}
              </>
            ) : (
              <div className={cn('grid grid-cols-1 gap-(--space-6) md:grid-cols-2 md:items-start')}>
                <DadosTab form={form} onCepBlur={lookupCep} agencyId={agencyId} propertyId={editingProperty?.id} />
                <DescricaoTab key={mediaKey} {...descricaoProps} showMobileHeading={false} />
              </div>
            )}

            <div className="mt-(--space-5)">
              <PropertyPreviewBar values={watchedValues} />
            </div>

            {validationMessage && (
              <div data-testid="property-validation-summary" style={validationSummaryStyle}>
                {validationMessage}
              </div>
            )}
          </div>
        </AgencyRequiredFieldsContext.Provider>
      </AdaptiveFormShell>

      {publishDialogPropertyId && (
        <PublishDialog
          propertyId={publishDialogPropertyId}
          agencyId={agencyId}
          propertyImages={collectImageRefs(
            editingProperty?.media?.images ?? editingProperty?.images,
          )}
          onClose={() => setPublishDialogPropertyId(null)}
        />
      )}
    </>
  );
}
